import Form from '@shared/form'
import FormInput from '@shared/form-input'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import hasPerms from '@utils/has-permission'
import remove from 'lodash.remove'
import uniq from 'lodash.uniq'
import useAllActions from '@hooks/use-all-actions'
import useSelector from '@hooks/use-selector'
import { AppDispatch, UserFormType } from '@types'
import {
  Button,
  CircularProgress,
  List,
  ListItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { callIf } from '@utils/functional'
import { createUser, getUser, saveUser } from '@store/actions/users'
import { makeStyles } from 'tss-react/mui'
import {
  permissionListAlpha,
  rolesListAlpha,
  storesListAlpha,
} from '@store/selectors'
import { selectUserById } from '@store/selectors/users'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

const useSubmitUpdateUser = (user: UserFormType, isNewUser: boolean) => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const saveFn = isNewUser ? createUser : saveUser

  return async () => {
    const updatedUser = await dispatch(saveFn(user))

    if (updatedUser) {
      navigate(`/users/${updatedUser.id}`)
    }
  }
}

const useStyles = makeStyles()((theme) => ({
  submitButton: {
    marginTop: theme.spacing(3),
    color: 'white',
  },
  section: {
    marginBottom: theme.spacing(2),
    display: 'flex',
  },
  block: {
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    width: 'auto',
  },
  label: { cursor: 'pointer' },
  stores: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
}))

type Props = {
  className: any
  title?: string
}

const newUser = {
  name: '',
  email: '',
  phone_number: '',
  role_ids: [],
  permission_ids: [],
  photo_url: null,
  store_ids: [],
  manager_approval_code: '',
  loyalty_number: '',
  birthdate: '',
} as UserFormType

const UserForm = ({ className }: Props): ReactElement => {
  const dispatch = useDispatch<AppDispatch>()
  const { id } = useParams<{ id: string }>()
  const { classes } = useStyles()
  const canUpdateUsersRolesPermissions = hasPerms('roles:update')
  const canUpdateLoyaltyNumber = hasPerms('loyalty_number:update')
  const canUpdateEmail = hasPerms('users:update-email')
  const canUpdateManagerApprovalCode = hasPerms(
    'users:update-manager_approval_code',
  )

  const isNewUser = id === undefined

  // TODO: selector should not be returning a different object on re-render
  const initialUser = useSelector((state) => selectUserById(state, id))
  const roles = useSelector(rolesListAlpha)
  const permissions = useSelector(permissionListAlpha)
  const stores = useSelector(storesListAlpha)
  const currentUserId = useSelector((state) => state.CurrentUser.user.id)

  const { setFlashMessage, getRoles, getStores } = useAllActions()

  const [user, setUser] = useState<UserFormType>(initialUser || newUser)
  const [rolePermIds, setRolePermIds] = useState<number[]>([])

  const [isLoaded, setIsLoaded] = useState(false)

  const userRoleIds = useMemo(() => user.role_ids, [user])

  const submitUpdateUser = useSubmitUpdateUser(user, isNewUser)

  const canViewManagerApprovalCode =
    canUpdateManagerApprovalCode || (user?.id && currentUserId == user?.id)

  // updates default role permission ids
  useEffect(() => {
    if (roles.length === 0 || !roles) return

    const permIds = userRoleIds.map((roleId: number) => {
      return roles.find((role) => role.id === roleId)?.permission_ids
    })

    setRolePermIds(permIds.flat())
  }, [userRoleIds, roles])

  useEffect(() => {
    if (initialUser && !isNewUser) {
      setUser(initialUser)
    }
  }, [initialUser])

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        callIf(!isNewUser, async () => await dispatch(getUser(id as string))),
        getRoles(),
        getStores(),
      ])
      setIsLoaded(true)
    }

    loadData()
  }, [id])

  const allStoresPermission = useMemo(
    () => permissions.find((p) => p.permission == 'stores:update-all'),
    [permissions],
  )

  if (!isLoaded) return <CircularProgress />

  const updateUser = (value, e) => {
    const name = e.target.name

    setUser({ ...user, [name]: value })
  }

  const updateManagerApprovalCode = (value, e) => {
    value = value.trim().slice(0, 6)

    updateUser(value, e)
  }

  const togglePermission = (e, value: boolean) => {
    const permId = parseInt(e.target.name, 10)
    const permIds = [...user.permission_ids]

    if (value) {
      permIds.push(permId)
    } else {
      remove(permIds, (i) => i === permId)
    }

    setUser({ ...user, permission_ids: uniq(permIds) })
  }

  const toggleRole = (e, value: boolean) => {
    const roleId = parseInt(e.target.name, 10)
    const roleIds = [...user.role_ids]
    const permissionIds = [...user.permission_ids]

    if (value) {
      roleIds.push(roleId)
    } else {
      remove(roleIds, (i) => i === roleId)
      const rolePermIds = roles.find(
        (role) => role.id === roleId,
      )?.permission_ids
      remove(permissionIds, (i) => rolePermIds.includes(i))
    }

    setUser({
      ...user,
      role_ids: uniq(roleIds),
      permission_ids: uniq(permissionIds),
    })
  }

  const toggleStore = (e, value: boolean) => {
    const storeId = parseInt(e.target.name, 10)
    const storeIds = [...user.store_ids]

    if (value) {
      storeIds.push(storeId)
    } else {
      remove(storeIds, (i) => i === storeId)
    }

    setUser({ ...user, store_ids: uniq(storeIds) })
  }

  const handleSubmit = async () => {
    submitUpdateUser()
  }

  const hasAllStoresPermission =
    user.permission_ids.includes(allStoresPermission?.id) ||
    rolePermIds.includes(allStoresPermission?.id)

  return (
    <Form className={className} title={isNewUser ? 'Create User' : 'Edit User'}>
      <FormInput
        name='name'
        onChangeText={updateUser}
        title='Name'
        value={user.name}
      />
      <FormInput
        inputProps={{
          disabled: !canUpdateEmail,
        }}
        name='email'
        onChangeText={updateUser}
        title='Email'
        value={user.email || ''}
      />
      <FormInput
        inputProps={{
          type: 'number',
        }}
        name='phone_number'
        onChangeText={updateUser}
        title='Phone Number'
        value={user.phone_number || ''}
      />
      {canViewManagerApprovalCode && (
        <FormInput
          inputProps={{
            disabled: !canUpdateManagerApprovalCode,
          }}
          name='manager_approval_code'
          onChangeText={updateManagerApprovalCode}
          title='Manager Approval Code (6 Characters)'
          value={user.manager_approval_code ?? ''}
        />
      )}
      <FormInput
        inputProps={{
          disabled: !canUpdateLoyaltyNumber,
        }}
        name='loyalty_number'
        onChangeText={updateUser}
        title='Loyalty Number'
        value={user.loyalty_number ?? ''}
      />
      <FormInput
        name='birthdate'
        onChangeText={updateUser}
        placeholder='09/16'
        title='Birthday'
        value={user.birthdate ?? ''}
      />

      <div className={classes.section}>
        <div className={classes.block}>
          <Typography variant='h6'>Roles</Typography>
          {roles.map((role) => {
            return (
              <div key={`edit_role_${role.id}`}>
                <Switch
                  checked={user.role_ids.includes(role.id)}
                  disabled={
                    !canUpdateUsersRolesPermissions || role.role === 'barista'
                  }
                  id={`edit_role_${role.id}`}
                  name={role.id.toString()}
                  onChange={toggleRole}
                />
                <label
                  className={classes.label}
                  htmlFor={`edit_role_${role.id}`}
                >
                  {role.role}
                </label>
              </div>
            )
          })}
        </div>
        <div className={classes.block}>
          <Typography variant='h6'>Permissions</Typography>
          <List>
            {permissions.map((permission) => {
              const defaultPerm = rolePermIds.includes(permission.id)
              const checked =
                defaultPerm || user.permission_ids.includes(permission.id)
              return (
                <ListItem key={`edit_permission_${permission.id}`}>
                  <Switch
                    checked={checked}
                    disabled={!canUpdateUsersRolesPermissions || defaultPerm}
                    id={`edit_permission_${permission.id}`}
                    name={permission.id.toString()}
                    onChange={togglePermission}
                  />
                  <Tooltip title={permission.description}>
                    <label
                      className={classes.label}
                      htmlFor={`edit_permission_${permission.id}`}
                    >
                      {permission.permission}
                    </label>
                  </Tooltip>
                </ListItem>
              )
            })}
          </List>
        </div>
        <div className={classes.block}>
          <Typography variant='h6'>Stores</Typography>
          <List className={classes.stores}>
            <ListItem key='edit_store_all'>
              <Switch
                checked={hasAllStoresPermission}
                disabled={!canUpdateUsersRolesPermissions}
                id='edit_store_all'
                name={allStoresPermission?.id.toString()}
                onChange={togglePermission}
              />
              <Tooltip title='Apply users permissions to all stores'>
                <label className={classes.label} htmlFor='edit_store_all'>
                  All Stores
                </label>
              </Tooltip>
            </ListItem>
            {stores.map((store) => {
              return (
                <ListItem key={`edit_store_${store.id}`}>
                  <Switch
                    checked={
                      hasAllStoresPermission ||
                      user.store_ids?.includes(store.id)
                    }
                    disabled={
                      !canUpdateUsersRolesPermissions || hasAllStoresPermission
                    }
                    id={`edit_store_${store.id}`}
                    name={store?.id.toString()}
                    onChange={toggleStore}
                  />
                  <label
                    className={classes.label}
                    htmlFor={`edit_store_${store.id}`}
                  >
                    {store.name}
                  </label>
                </ListItem>
              )
            })}
          </List>
        </div>
      </div>
      <Button
        className={classes.submitButton}
        color='secondary'
        onClick={async () => {
          try {
            handleSubmit()
          } catch (error) {
            setFlashMessage(error, 'error')
          }
        }}
        variant='contained'
      >
        Submit
      </Button>
    </Form>
  )
}

export default UserForm
